import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  normalizeProposalClientTeamRoster,
  rebaseProposalClientTeamRoster,
  type ProposalClientTeamMemberReference,
} from "work/proposals/negotiation/proposal-client-team-roster";
import {
  proposalClientTeamMembersStaged,
  selectAuthoritativeProposalClientTeamRoster,
  selectProposalClientTeamRosterDraft,
  selectProposalClientTeamRosterMovedUnderEdit,
  selectProposalHasPendingClientTeamMembers,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";

export type ProposalClientTeamRoster = {
  /** The roster to draw: the viewer's unsaved edit where there is one, otherwise the saved roster. */
  members: EntityMember[];
  /** Whether an edit is waiting for the page's Save. */
  hasUnsavedChanges: boolean;
  /** Whether somebody else's roster change landed underneath that unsaved edit. */
  changedElsewhereWhileEditing: boolean;
  /** Records the roster the viewer means the client team to have. Writes nothing. */
  stageMembers: (members: readonly EntityMember[]) => void;
};

/**
 * The client-team roster a proposal tab edits, held until the page's Save writes it (#5116).
 *
 * The roster lives in the negotiation's collaboration entry rather than in the tab, for the same
 * reason every other tab's edits do: the tab is unmounted the moment the reader looks at Fee
 * Schedule, and an edit that disappears when you check something else is an edit the page has
 * lost. Being Redux state also gives it the rest of the editor's save behaviour for nothing — the
 * header Save enables, the discard-on-navigate prompt arms, and Save & Close carries it — because
 * they all read `hasPendingChanges`.
 *
 * Membership is still reference state written by the dedicated `client-team-members` command, not
 * a content operation; only *when* it is written has changed. See
 * `docs/design/proposals-negotiation/ARCHITECTURE.md`.
 *
 * Returns undefined where there is no negotiation to stage against — an uncreated draft, the
 * archived proposal, the hired work agreement — leaving those surfaces exactly as they were.
 */
export function useProposalClientTeamRoster(negotiationId?: string): ProposalClientTeamRoster | undefined {
  const dispatch = useAttorneyHubDispatch();
  const authoritativeSelector = useMemo(
    () => selectAuthoritativeProposalClientTeamRoster(negotiationId ?? ""),
    [negotiationId],
  );
  const draftSelector = useMemo(() => selectProposalClientTeamRosterDraft(negotiationId ?? ""), [negotiationId]);
  const dirtySelector = useMemo(
    () => selectProposalHasPendingClientTeamMembers(negotiationId ?? ""),
    [negotiationId],
  );
  const movedSelector = useMemo(
    () => selectProposalClientTeamRosterMovedUnderEdit(negotiationId ?? ""),
    [negotiationId],
  );
  const authoritative = useSelector(authoritativeSelector);
  const draft = useSelector(draftSelector);
  const hasUnsavedChanges = useSelector(dirtySelector);
  const changedElsewhereWhileEditing = useSelector(movedSelector);

  // A deliberate stability guarantee, not defensive memoization (see app/CLAUDE.md): the roster is
  // rebuilt on every render and reaches the dependency array of the tab's individual-lookup query,
  // which would otherwise re-run on every render of the page. Both inputs are themselves memoized
  // selector results, so this identity changes exactly when the roster does.
  const roster = useMemo(
    () => (draft ? rebaseProposalClientTeamRoster(draft, authoritative) : authoritative),
    [authoritative, draft],
  );
  const members = useMemo(() => roster.map(toEntityMember), [roster]);

  const stageMembers = useCallback(
    (nextMembers: readonly EntityMember[]) => {
      if (!negotiationId) return;
      dispatch(
        proposalClientTeamMembersStaged({
          negotiationId,
          members: normalizeProposalClientTeamRoster(nextMembers.map(toMemberReference)),
          // The base is the roster the server holds right now, so a later remote change can be
          // told from this viewer's own edit and merged rather than overwritten.
          baseline: authoritative,
        }),
      );
    },
    [authoritative, dispatch, negotiationId],
  );

  if (!negotiationId) return undefined;
  return { members, hasUnsavedChanges, changedElsewhereWhileEditing, stageMembers };
}

function toEntityMember(member: ProposalClientTeamMemberReference): EntityMember {
  return new EntityMember({ userId: new Guid(member.userId), legalEntityId: new Guid(member.legalEntityId) });
}

function toMemberReference(member: EntityMember): ProposalClientTeamMemberReference {
  return { userId: member.userId?.value ?? "", legalEntityId: member.legalEntityId?.value ?? "" };
}
